import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { DEFAULT_THEME } from '../data/store';

const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  setTheme: () => {},
  resetTheme: () => {},
});

// Convert any user-typed value (named color / hex / rgb) into a renderable color.
// In practice we accept it as-is and rely on the browser. Light validation only.
const normalizeColor = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed;
};

const sanitizeTheme = (theme) => {
  const base = theme || {};
  return {
    primary: normalizeColor(base.primary) || DEFAULT_THEME.primary,
    secondary: normalizeColor(base.secondary) || DEFAULT_THEME.secondary,
    tertiary: normalizeColor(base.tertiary) || DEFAULT_THEME.tertiary,
  };
};

const applyToDocument = (theme) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const clean = sanitizeTheme(theme);
  root.style.setProperty('--theme-primary', clean.primary);
  root.style.setProperty('--theme-secondary', clean.secondary);
  root.style.setProperty('--theme-tertiary', clean.tertiary);
  // Derived shades (browser-computed via color-mix) used by hovers + tints.
  root.style.setProperty('--theme-primary-dark', `color-mix(in srgb, ${clean.primary} 70%, #000 30%)`);
  root.style.setProperty('--theme-primary-soft', `color-mix(in srgb, ${clean.primary} 8%, transparent)`);
  root.style.setProperty('--theme-primary-softer', `color-mix(in srgb, ${clean.primary} 4%, transparent)`);
  root.style.setProperty('--theme-primary-tint', `color-mix(in srgb, ${clean.primary} 18%, transparent)`);
};

export const ThemeProvider = ({ initialTheme, children }) => {
  const [theme, setThemeState] = useState(() => sanitizeTheme(initialTheme));

  useEffect(() => { applyToDocument(theme); }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState((prev) => {
      const clean = sanitizeTheme(next);
      if (prev.primary === clean.primary && prev.secondary === clean.secondary && prev.tertiary === clean.tertiary) {
        return prev;
      }
      return clean;
    });
  }, []);

  const resetTheme = useCallback(() => {
    setThemeState((prev) => {
      if (prev.primary === DEFAULT_THEME.primary && prev.secondary === DEFAULT_THEME.secondary && prev.tertiary === DEFAULT_THEME.tertiary) {
        return prev;
      }
      return DEFAULT_THEME;
    });
  }, []);

  const value = useMemo(() => ({ theme, setTheme, resetTheme }), [theme, setTheme, resetTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

ThemeProvider.propTypes = {
  initialTheme: PropTypes.shape({
    primary: PropTypes.string,
    secondary: PropTypes.string,
    tertiary: PropTypes.string,
  }),
  children: PropTypes.node,
};

export const useTheme = () => useContext(ThemeContext);
